import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { Button, Tabs, Tag } from 'antd';
import {
    clearApiAccessUpdatePasswordStatus,
    clearApiAccessUpdateUserStatus,
    clearApiAccessDetails,
    getApiAccessRolesRequest,
    getApiAccessUserEndpointsRequest,
    getApiAccessUserRequest,
    updateApiAccessPasswordRequest,
    updateApiAccessUserRequest,
} from 'redux/actions/configProvider/api-access-actions';
import {
    getApiAccessRoles,
    getApiAccessUserDetails,
    getApiAccessUserEndpoints,
    getApiAccessUserEndpointsLoading,
    getApiAccessUserLoading,
    getApiAccessUpdatePasswordLoading,
    getApiAccessUpdatePasswordResult,
    getApiAccessUpdatePasswordSuccess,
    getApiAccessUpdateUserLoading,
    getApiAccessUpdateUserSuccess,
} from 'redux/selectors/configProvider/api-access-selectors';
import ApiAccessBrandsTab from './ApiAccessBrandsTab';
import ApiAccessEndpointsTab from './ApiAccessEndpointsTab';
import ApiAccessGeneratedPasswordModal from './ApiAccessGeneratedPasswordModal';
import ApiAccessPasswordModal from './ApiAccessPasswordModal';
import ApiAccessUserModal, { IApiAccessUserFormValues } from './ApiAccessUserModal';

type TabKey = 'brands' | 'endpoints';

interface IApiAccessUser {
    id: number;
    name?: string;
    username?: string;
    principalType?: string;
    blocked?: boolean;
    role?: string;
    roles?: Array<string | { id?: number; name?: string }>;
    authorities?: string[];
}

const normalizeRoleValue = (role: string | { id?: number; name?: string } | undefined) => {
    if (typeof role === 'string' && role) {
        return role;
    }

    if (role && typeof role !== 'string' && typeof role.name === 'string' && role.name) {
        return role.name;
    }

    return null;
};

const getUserRoles = (user?: IApiAccessUser | null) => {
    if (!user) {
        return [];
    }

    if (user.roles && user.roles.length > 0) {
        return user.roles
            .map((role) => normalizeRoleValue(role))
            .filter((role): role is string => Boolean(role));
    }

    if (typeof user.role === 'string' && user.role) {
        return [user.role];
    }

    return (user.authorities || []).filter(
        (role): role is string => typeof role === 'string' && Boolean(role),
    );
};

const ApiAccessDetails: React.FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const roles = useSelector((state: any) => getApiAccessRoles(state));
    const userDetails = useSelector((state: any) => getApiAccessUserDetails(state));
    const endpoints = useSelector((state: any) => getApiAccessUserEndpoints(state));
    const userLoading = useSelector((state: any) => getApiAccessUserLoading(state));
    const endpointsLoading = useSelector((state: any) =>
        getApiAccessUserEndpointsLoading(state),
    );
    const updateUserLoading = useSelector((state: any) => getApiAccessUpdateUserLoading(state));
    const updateUserSuccess = useSelector((state: any) => getApiAccessUpdateUserSuccess(state));
    const updatePasswordLoading = useSelector((state: any) =>
        getApiAccessUpdatePasswordLoading(state),
    );
    const updatePasswordResult = useSelector((state: any) =>
        getApiAccessUpdatePasswordResult(state),
    );
    const updatePasswordSuccess = useSelector((state: any) =>
        getApiAccessUpdatePasswordSuccess(state),
    );
    const { operatorId } = useParams();
    const userId = Number(operatorId);
    const hasValidUserId = Number.isFinite(userId);
    const [activeTab, setActiveTab] = React.useState<TabKey>('brands');
    const [isEditModalOpen, setIsEditModalOpen] = React.useState(false);
    const [isPasswordModalOpen, setIsPasswordModalOpen] = React.useState(false);
    const [isGeneratedPasswordModalOpen, setIsGeneratedPasswordModalOpen] = React.useState(false);

    const fetchDetails = React.useCallback(() => {
        if (!hasValidUserId) {
            return;
        }

        dispatch(getApiAccessUserRequest(userId));
        dispatch(getApiAccessUserEndpointsRequest(userId));
    }, [dispatch, hasValidUserId, userId]);

    React.useEffect(() => {
        dispatch(getApiAccessRolesRequest());
        fetchDetails();

        return () => {
            dispatch(clearApiAccessDetails());
        };
    }, [dispatch, fetchDetails]);

    const roleOptions = React.useMemo(
        () =>
            (roles || []).map((role: { name?: string }) => ({
                label: normalizeRoleValue(role)?.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase()) || '',
                value: normalizeRoleValue(role) || '',
            })).filter((role: { label: string; value: string }) => Boolean(role.value)),
        [roles],
    );

    React.useEffect(() => {
        if (updateUserSuccess) {
            setIsEditModalOpen(false);
            fetchDetails();
            dispatch(clearApiAccessUpdateUserStatus());
        }
    }, [dispatch, fetchDetails, updateUserSuccess]);

    React.useEffect(() => {
        if (updatePasswordSuccess) {
            setIsPasswordModalOpen(false);
            setIsGeneratedPasswordModalOpen(true);
        }
    }, [dispatch, updatePasswordSuccess]);

    const handleUpdateUser = React.useCallback((values: IApiAccessUserFormValues) => {
        if (!userDetails?.id) {
            return;
        }

        dispatch(updateApiAccessUserRequest({
            id: userDetails.id,
            username: values.username.trim(),
            blocked: !values.enabled,
            roles: values.roles,
        }));
    }, [dispatch, userDetails]);

    const handleUpdatePassword = React.useCallback(() => {
        if (!userDetails?.id) {
            return;
        }

        dispatch(updateApiAccessPasswordRequest({
            id: userDetails.id,
        }));
    }, [dispatch, userDetails]);

    const handleToggleStatus = React.useCallback(() => {
        if (!userDetails?.id) {
            return;
        }

        dispatch(updateApiAccessUserRequest({
            id: userDetails.id,
            username: (userDetails.username || userDetails.name || `${userDetails.id}`).trim(),
            blocked: !userDetails.blocked,
            roles: getUserRoles(userDetails),
        }));
    }, [dispatch, userDetails]);

    const userName =
        userDetails?.username || userDetails?.name || (hasValidUserId ? `${userId}` : '');
    const isBlocked = userDetails?.blocked;

    return (
        <div className="min-h-0 h-[calc(100vh-64px)] overflow-hidden bg-[#fafbfc] flex flex-col pb-4">
            <div className="px-6 pt-6 pb-4">
                <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                    <div className="flex items-start gap-3">
                        <Button
                            icon={<ArrowLeftOutlined />}
                            type="text"
                            onClick={() => navigate(-1)}
                            className="!mt-4 !px-0 !text-gray-400 hover:!text-gray-600"
                        />
                        <div>
                            <div className="mb-1 text-xs text-gray-400">
                                <Link to="/config-provider/api-access" className="text-gray-400">
                                    API Access
                                </Link>
                            </div>
                            <div className="flex items-center gap-3">
                                <h1 className="m-0 text-[18px] font-semibold text-gray-900 md:text-[20px]">
                                    {userName}
                                </h1>
                                {typeof isBlocked === 'boolean' && (
                                    <Tag
                                        className={`!mr-0 !rounded-md !px-3 !py-0.5 !text-sm !font-medium ${
                                            isBlocked
                                                ? '!border-[#ffccc7] !bg-[#fff2f0] !text-[#ff4d4f]'
                                                : '!border-[#b7eb8f] !bg-[#f6ffed] !text-[#52c41a]'
                                        }`}
                                    >
                                        {isBlocked ? 'Blocked' : 'Active'}
                                    </Tag>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                        <Button
                            shape="round"
                            onClick={() => setIsEditModalOpen(true)}
                            disabled={!userDetails?.id}
                        >
                            Edit
                        </Button>
                        <Button
                            shape="round"
                            onClick={() => setIsPasswordModalOpen(true)}
                            disabled={!userDetails?.id}
                        >
                            Reset Password
                        </Button>
                        <Button
                            shape="round"
                            onClick={handleToggleStatus}
                            loading={updateUserLoading}
                            disabled={!userDetails?.id}
                        >
                            {isBlocked ? 'Enable' : 'Disable'}
                        </Button>
                    </div>
                </div>
            </div>

            <div className="flex-1 overflow-auto px-6">
                <div className="min-h-[calc(100vh-210px)] rounded-lg border border-gray-200 bg-white">
                    <Tabs
                        activeKey={activeTab}
                        onChange={(key) => setActiveTab(key as TabKey)}
                        className="[&_.ant-tabs-nav]:!mb-0 [&_.ant-tabs-nav]:!px-4 [&_.ant-tabs-nav]:!pt-1 [&_.ant-tabs-tab]:!pb-4 [&_.ant-tabs-tab-active_.ant-tabs-tab-btn]:!text-[#df1f26] [&_.ant-tabs-tab-btn]:!text-[15px] [&_.ant-tabs-ink-bar]:!bg-[#df1f26] [&_.ant-tabs-content-holder]:border-t [&_.ant-tabs-content-holder]:border-gray-100"
                        items={[
                            {
                                key: 'brands',
                                label: 'Brands',
                                children: (
                                    <div className="p-5">
                                        <ApiAccessBrandsTab
                                            user={userDetails}
                                            loading={userLoading}
                                        />
                                    </div>
                                ),
                            },
                            {
                                key: 'endpoints',
                                label: 'API Endpoints',
                                children: (
                                    <div className="p-5">
                                        <ApiAccessEndpointsTab
                                            endpoints={endpoints}
                                            loading={endpointsLoading}
                                        />
                                    </div>
                                ),
                            },
                        ]}
                    />
                </div>
            </div>

            <ApiAccessUserModal
                mode="edit"
                open={isEditModalOpen}
                loading={updateUserLoading}
                roleOptions={roleOptions}
                initialValues={
                    userDetails
                        ? {
                              username: userDetails.username || userDetails.name || '',
                              enabled: !userDetails.blocked,
                              principalType: userDetails.principalType || 'THIRD_PARTY',
                              roles: getUserRoles(userDetails),
                          }
                        : undefined
                }
                onCancel={() => {
                    setIsEditModalOpen(false);
                    dispatch(clearApiAccessUpdateUserStatus());
                }}
                onSubmit={handleUpdateUser}
            />

            <ApiAccessPasswordModal
                open={isPasswordModalOpen}
                loading={updatePasswordLoading}
                username={userDetails?.username || userDetails?.name}
                onCancel={() => {
                    setIsPasswordModalOpen(false);
                    dispatch(clearApiAccessUpdatePasswordStatus());
                }}
                onSubmit={handleUpdatePassword}
            />

            <ApiAccessGeneratedPasswordModal
                open={isGeneratedPasswordModalOpen}
                password={updatePasswordResult?.rawPassword}
                username={userDetails?.username || userDetails?.name}
                title="New Password Generated"
                description="Copy the new password now, because it will not be shown again."
                onCancel={() => {
                    setIsGeneratedPasswordModalOpen(false);
                    dispatch(clearApiAccessUpdatePasswordStatus());
                }}
            />
        </div>
    );
};

export default ApiAccessDetails;
